import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import { visit } from 'unist-util-visit';
import GithubSlugger from 'github-slugger';

/**
 * Strips YAML frontmatter from markdown content
 * @param {string} markdown - The markdown content with frontmatter
 * @returns {string} Markdown without frontmatter
 */
function stripFrontmatter(markdown) {
  // Match YAML frontmatter (--- at start, content, --- at end)
  const frontmatterRegex = /^---\s*\n[\s\S]*?\n---\s*\n/;
  return markdown.replace(frontmatterRegex, '');
}

/**
 * Extracts table of contents from markdown content
 * @param {string} markdown - The markdown content
 * @returns {Array} Array of TOC items with structure: { id, text, level, children }
 */
export function extractToc(markdown) {
  const slugger = new GithubSlugger();
  const toc = [];

  // Strip frontmatter before processing
  const cleanMarkdown = stripFrontmatter(markdown);

  // Parse markdown into AST
  const tree = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .parse(cleanMarkdown);

  // Extract headings from AST
  const headings = [];
  visit(tree, 'heading', (node) => {
    if (node.depth === 2 || node.depth === 3) {
      const text = extractText(node);
      const id = slugger.slug(text);
      headings.push({
        id,
        text,
        level: node.depth,
        children: []
      });
    }
  });

  // Build hierarchical structure (H2 as parents, H3 as children)
  let currentH2 = null;

  for (const heading of headings) {
    if (heading.level === 2) {
      currentH2 = heading;
      toc.push(heading);
    } else if (heading.level === 3 && currentH2) {
      currentH2.children.push(heading);
    }
  }

  return toc;
}

/**
 * Extracts plain text from a heading node
 * @param {Object} node - The heading AST node
 * @returns {string} Plain text content
 */
function extractText(node) {
  let text = '';

  visit(node, 'text', (textNode) => {
    text += textNode.value;
  });

  return text;
}
